import { api, type Page } from '@/shared/api/client';
import { apiUrl } from '@/shared/config';

import type {
  Document,
  DocumentDetail,
  DocumentEdit,
  DocumentKind,
  DocumentUrl,
  MonthCount,
} from './types';

export type DocumentView = 'live' | 'trash' | 'versions';

export interface DocumentListParams {
  kind?: DocumentKind | undefined;
  counterparty?: string | undefined;
  reference?: string | undefined;
  year?: number | undefined;
  month?: number | undefined;
  since?: string | undefined;
  until?: string | undefined;
  q?: string | undefined;
  view?: DocumentView | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export interface DocumentUpload {
  file: File;
  kind: DocumentKind;
  counterparty?: string | undefined;
  reference?: string | undefined;
  document_date?: string | undefined;
  sensitive?: boolean | undefined;
  notes?: string | undefined;
}

export const documentKeys = {
  all: ['documents'] as const,
  list: (params: DocumentListParams) => ['documents', 'list', params] as const,
  months: ['documents', 'months'] as const,
  detail: (id: string) => ['documents', 'detail', id] as const,
};

export const DOCUMENTS_PAGE = 50;

function formOf(upload: Omit<DocumentUpload, 'kind'> & { kind?: DocumentKind }): FormData {
  const form = new FormData();
  form.append('file', upload.file, upload.file.name);
  if (upload.kind) form.append('kind', upload.kind);
  if (upload.counterparty) form.append('counterparty', upload.counterparty);
  if (upload.reference) form.append('reference', upload.reference);
  if (upload.document_date) form.append('document_date', upload.document_date);
  if (upload.sensitive !== undefined) form.append('sensitive', String(upload.sensitive));
  if (upload.notes) form.append('notes', upload.notes);
  return form;
}

/** The ZIP of a month: a plain link, the browser downloads it with the session cookie. */
export function exportUrl(year: number, month: number): string {
  return `${apiUrl}/api/documents/export.zip?year=${year}&month=${month}`;
}

export const documentsApi = {
  list: (params: DocumentListParams): Promise<Page<Document>> =>
    api.get<Page<Document>>('/api/documents', {
      ...params,
      limit: params.limit ?? DOCUMENTS_PAGE,
      offset: params.offset ?? 0,
    }),
  months: (): Promise<MonthCount[]> => api.get<MonthCount[]>('/api/documents/months'),
  detail: (id: string): Promise<DocumentDetail> => api.get<DocumentDetail>(`/api/documents/${id}`),
  url: (id: string, disposition: 'attachment' | 'inline'): Promise<DocumentUrl> =>
    api.get<DocumentUrl>(`/api/documents/${id}/url`, { disposition }),
  upload: (upload: DocumentUpload): Promise<Document> =>
    api.postForm<Document>('/api/documents', formOf(upload)),
  replace: (id: string, file: File): Promise<Document> =>
    api.postForm<Document>(`/api/documents/${id}/replace`, formOf({ file })),
  edit: (id: string, body: DocumentEdit): Promise<Document> =>
    api.patch<Document>(`/api/documents/${id}`, body),
  trash: (id: string, reason: string): Promise<Document> =>
    api.delete<Document>(`/api/documents/${id}`, { reason }),
  restore: (id: string): Promise<Document> => api.post<Document>(`/api/documents/${id}/restore`),
};
